// Click-to-revive: bring a string's letters back into the typing box,
// each arriving at its correct wrapped position (line-by-line, top → bottom).
//
// The target positions come from laying out the revived text invisibly in
// the typing box (visibility: hidden via a CSS class) and probing each
// character's getBoundingClientRect.

import Matter from 'matter-js';
import { state, type ReviveState } from '../state.js';
import { getLetterPlugin, isLetterBody } from '../physics/letterBody.js';
import { wakeAllBodies } from '../physics/engine.js';
import { probeCharRects } from './dropString.js';

const MOVE_DURATION_MS = 420;
const INTER_LINE_DELAY_MS = 60;
const LINE_BUCKET_PX = 8; // tolerance when grouping rects into lines

// Find the letter body under a screen point.
export function findLetterAt(
  engine: Matter.Engine,
  point: { x: number; y: number },
): Matter.Body | null {
  const bodies = Matter.Composite.allBodies(engine.world).filter(isLetterBody);
  const hits = Matter.Query.point(bodies, point);
  return hits[0] ?? null;
}

/**
 * Begin a revive animation.
 *
 * The caller must have just set `typingEl.innerText` to the string to revive,
 * and added the `.reviving` class so the text is invisibly laid out.
 *
 * `readOnly` keeps the letters in the world and static at the typing-box
 * target positions once the tween finishes, so a non-author can still see
 * the note but nobody modifies it. Call `startDismiss` to animate them back.
 */
export function startRevive(
  engine: Matter.Engine,
  stringId: string,
  typingEl: HTMLElement,
  opts?: { readOnly?: boolean },
): boolean {
  if (state.revive) return false;
  const str = state.strings.get(stringId);
  if (!str) return false;
  const readOnly = Boolean(opts?.readOnly);

  // Gather all bodies belonging to this string, indexed by charIndex for fast lookup.
  const allBodies = Matter.Composite.allBodies(engine.world);
  const bodiesByCharIndex = new Map<number, Matter.Body>();
  for (const b of allBodies) {
    const plugin = getLetterPlugin(b);
    if (!plugin || plugin.stringId !== stringId) continue;
    bodiesByCharIndex.set(plugin.charIndex, b);
  }
  if (bodiesByCharIndex.size === 0) return false;

  // Probe the now-laid-out typing box for target rects.
  const rects = probeCharRects(typingEl);
  if (rects.length === 0) return false;

  // Group targets into lines (by rect.top rounded to LINE_BUCKET_PX) and sort
  // top → bottom. Within a line, bodies keep their natural reading order.
  type Line = { top: number; entries: Array<typeof rects[number]> };
  const linesByTop = new Map<number, Line>();
  for (const entry of rects) {
    const bucket = Math.round(entry.rect.top / LINE_BUCKET_PX) * LINE_BUCKET_PX;
    let line = linesByTop.get(bucket);
    if (!line) {
      line = { top: bucket, entries: [] };
      linesByTop.set(bucket, line);
    }
    line.entries.push(entry);
  }
  const sortedLines = Array.from(linesByTop.values()).sort((a, b) => a.top - b.top);

  const targets = new Map<number, { x: number; y: number; angle: number; startDelayMs: number }>();
  const starts = new Map<number, { x: number; y: number; angle: number }>();
  const bodyIds: number[] = [];
  let maxDelay = 0;

  for (let lineIdx = 0; lineIdx < sortedLines.length; lineIdx++) {
    const line = sortedLines[lineIdx]!;
    const startDelayMs = lineIdx * INTER_LINE_DELAY_MS;
    if (startDelayMs > maxDelay) maxDelay = startDelayMs;

    for (const entry of line.entries) {
      const body = bodiesByCharIndex.get(entry.charIndex);
      if (!body) continue;
      const cx = entry.rect.left + entry.rect.width / 2;
      const cy = entry.rect.top + entry.rect.height / 2;
      targets.set(body.id, { x: cx, y: cy, angle: 0, startDelayMs });
      starts.set(body.id, {
        x: body.position.x,
        y: body.position.y,
        angle: body.angle,
      });
      bodyIds.push(body.id);
      // Freeze physics during the tween.
      Matter.Body.setStatic(body, true);
    }
  }

  if (bodyIds.length === 0) return false;

  const revive: ReviveState = {
    stringId,
    startTime: performance.now(),
    totalDurationMs: maxDelay + MOVE_DURATION_MS,
    bodyIds,
    targets,
    starts,
    readOnly,
    reverse: false,
  };
  state.revive = revive;
  return true;
}

/**
 * Unfreeze the held read-only letters in place so gravity takes over — they
 * fall from the typing-box positions down into the pile instead of tweening
 * back to their original spots. Used when a non-author presses Enter during
 * a read-only hold: the note "breaks apart" and the stage is reset.
 */
export function breakReadOnlyHold(engine: Matter.Engine): boolean {
  const hold = state.readOnlyHold;
  if (!hold) return false;

  const byId = new Map<number, Matter.Body>();
  for (const b of Matter.Composite.allBodies(engine.world)) byId.set(b.id, b);

  for (const id of hold.bodyIds) {
    const body = byId.get(id);
    if (!body) continue;
    Matter.Body.setStatic(body, false);
    // Tiny random kick so rows don't fall in a perfectly rigid line.
    Matter.Body.setVelocity(body, {
      x: (Math.random() - 0.5) * 1.8,
      y: Math.random() * 0.6,
    });
    Matter.Body.setAngularVelocity(body, (Math.random() - 0.5) * 0.12);
  }

  state.readOnlyHold = null;
  wakeAllBodies(engine);
  return true;
}

/**
 * Begin a reverse-dismiss tween: the letters currently held static at the
 * typing-box positions fly back into the pile and regain dynamics. Requires
 * `state.readOnlyHold` to have been set by the previous read-only revive.
 */
export function startDismiss(engine: Matter.Engine, stringId: string): boolean {
  if (state.revive) return false;
  const hold = state.readOnlyHold;
  if (!hold || hold.stringId !== stringId) return false;

  const byId = new Map<number, Matter.Body>();
  for (const b of Matter.Composite.allBodies(engine.world)) byId.set(b.id, b);

  const targets = new Map<number, { x: number; y: number; angle: number; startDelayMs: number }>();
  const starts = new Map<number, { x: number; y: number; angle: number }>();
  const bodyIds: number[] = [];
  for (const id of hold.bodyIds) {
    const body = byId.get(id);
    const pileStart = hold.pileStarts.get(id);
    if (!body || !pileStart) continue;
    starts.set(id, { x: body.position.x, y: body.position.y, angle: body.angle });
    targets.set(id, { x: pileStart.x, y: pileStart.y, angle: pileStart.angle, startDelayMs: 0 });
    bodyIds.push(id);
  }
  if (bodyIds.length === 0) {
    state.readOnlyHold = null;
    return false;
  }

  const revive: ReviveState = {
    stringId,
    startTime: performance.now(),
    totalDurationMs: MOVE_DURATION_MS,
    bodyIds,
    targets,
    starts,
    readOnly: false,
    reverse: true,
  };
  state.revive = revive;
  return true;
}

/** Advance the revive animation. Returns true once fully complete. */
export function stepRevive(engine: Matter.Engine, now: number): boolean {
  const revive = state.revive;
  if (!revive) return false;

  const elapsed = now - revive.startTime;

  const byId = new Map<number, Matter.Body>();
  for (const b of Matter.Composite.allBodies(engine.world)) byId.set(b.id, b);

  for (const id of revive.bodyIds) {
    const body = byId.get(id);
    if (!body) continue;
    const from = revive.starts.get(id);
    const to = revive.targets.get(id);
    if (!from || !to) continue;

    const localElapsed = elapsed - to.startDelayMs;
    const t = Math.max(0, Math.min(1, localElapsed / MOVE_DURATION_MS));
    const eased = easeOutCubic(t);

    Matter.Body.setPosition(body, {
      x: from.x + (to.x - from.x) * eased,
      y: from.y + (to.y - from.y) * eased,
    });
    Matter.Body.setAngle(body, from.angle + (to.angle - from.angle) * eased);
  }

  if (elapsed >= revive.totalDurationMs) {
    if (revive.reverse) {
      // Letters finished flying back into the pile — wake them up.
      for (const id of revive.bodyIds) {
        const body = byId.get(id);
        if (body) Matter.Body.setStatic(body, false);
      }
      state.readOnlyHold = null;
      return true;
    }
    if (revive.readOnly) {
      // Hold the letters static at the typing-box positions so the viewer
      // can read; the caller will flip the typing box into readonly mode.
      // Capture pile starts so startDismiss can animate back.
      state.readOnlyHold = {
        stringId: revive.stringId,
        bodyIds: revive.bodyIds.slice(),
        pileStarts: new Map(revive.starts),
      };
      return true;
    }
    // Editable revive: remove the bodies — the caller will accept the user's
    // edit and re-drop fresh letters from the typing box.
    for (const id of revive.bodyIds) {
      const body = byId.get(id);
      if (body) Matter.World.remove(engine.world, body);
    }
    return true;
  }
  return false;
}

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}
